import React, {
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect
} from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import LlmTreeNode from '../../../../types/BotBuilder/LlmTreeNode';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';
import CustomTooltip from '../../../../components/CustomTooltip/CustomTooltip';
import { Label } from '../../../../components/ui/label';
import useDebouncedEffect from '../../../../hooks/useDebouncedEffect';
import RichTextEditor from '../../../../components/RichTextEditor/RichTextEditor';
import PreviewModeContext from '../../../../context/PreviewModeContext/PreviewModeContext';
import {
  AI_MODEL_LABELS,
  DEFAULT_AI_MODEL,
  supportsTemperature
} from '../../../../types/RagAiModel';
import SlotValueType, {
  SlotValueTypeLabels
} from '../../../../types/BotBuilder/SlotValueType';

const AI_MODEL_OPTIONS = Object.entries(AI_MODEL_LABELS).map(
  ([value, label]) => ({ value, label })
);

export type LlmNodeData = {
  node: BotResponseTreeNode;
  isLocked?: boolean;
};

const isLlmTreeNode = (content: any): content is LlmTreeNode => {
  return content instanceof LlmTreeNode;
};

function LlmNode(props: NodeProps<LlmNodeData>): React.ReactElement {
  const { data, isConnectable } = props;
  const { onNodeUpdate, onNodeDelete } = useContext(BotBuilderContext);
  const { isPreviewMode, setIsPreviewMode } = useContext(PreviewModeContext);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  const llmNode = isLlmTreeNode(data.node.content) ? data.node.content : null;

  const [prompt, setPrompt] = useState<string>(llmNode?.prompt ?? '');
  const [outputVariableName, setOutputVariableName] = useState<string>(
    llmNode?.outputVariableName ?? ''
  );
  const [aiModel, setAiModel] = useState<string>(
    llmNode?.aiModel ?? DEFAULT_AI_MODEL
  );
  const [temperature, setTemperature] = useState<number>(
    llmNode?.temperature ?? 0.25
  );
  const [errorMessage, setErrorMessage] = useState<string>(
    llmNode?.errorMessage ?? ''
  );
  const [outputValueType, setOutputValueType] = useState<SlotValueType>(
    llmNode?.outputValueType ?? SlotValueType.FREE_TEXT
  );
  const [isEditingPrompt, setIsEditingPrompt] = useState<boolean>(false);

  const updateNodeData = useCallback(
    (updates: Partial<LlmTreeNode>) => {
      if (!llmNode) return;

      let hasChanges = false;
      Object.entries(updates).forEach(([key, value]) => {
        if (llmNode[key as keyof LlmTreeNode] !== value) {
          hasChanges = true;
        }
      });

      if (hasChanges) {
        Object.assign(llmNode, updates);
        onNodeUpdate(data.node);
      }
    },
    [llmNode, onNodeUpdate, data.node]
  );

  useEffect(() => {
    if (llmNode) {
      setPrompt(llmNode.prompt);
      setOutputVariableName(llmNode.outputVariableName);
      setAiModel(llmNode.aiModel);
      setTemperature(llmNode.temperature);
      setErrorMessage(llmNode.errorMessage);
      setOutputValueType(llmNode.outputValueType);
    }
  }, [
    llmNode?.prompt,
    llmNode?.outputVariableName,
    llmNode?.aiModel,
    llmNode?.temperature,
    llmNode?.errorMessage,
    llmNode?.outputValueType
  ]);

  useDebouncedEffect(
    () => {
      if (llmNode && prompt !== llmNode.prompt) {
        updateNodeData({ prompt });
      }
    },
    [prompt, llmNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (llmNode && outputVariableName !== llmNode.outputVariableName) {
        updateNodeData({ outputVariableName });
      }
    },
    [outputVariableName, llmNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (llmNode && aiModel !== llmNode.aiModel) {
        updateNodeData({ aiModel });
      }
    },
    [aiModel, llmNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (llmNode && temperature !== llmNode.temperature) {
        updateNodeData({ temperature });
      }
    },
    [temperature, llmNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (llmNode && errorMessage !== llmNode.errorMessage) {
        updateNodeData({ errorMessage });
      }
    },
    [errorMessage, llmNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (llmNode && outputValueType !== llmNode.outputValueType) {
        updateNodeData({ outputValueType });
      }
    },
    [outputValueType, llmNode, updateNodeData],
    300
  );

  const handlePromptEditEnd = useCallback(() => {
    if (llmNode && prompt !== llmNode.prompt) {
      updateNodeData({ prompt });
    }
    setIsEditingPrompt(false);
  }, [llmNode, prompt, updateNodeData]);

  const handlePromptEditStart = useCallback(
    (e: React.MouseEvent) => {
      if (data.isLocked) return;
      e.stopPropagation();
      setIsEditingPrompt(true);
      setIsPreviewMode(false);
    },
    [setIsPreviewMode, data.isLocked]
  );

  const handlePromptChange = useCallback((updatedContent: string) => {
    setPrompt(updatedContent || '');
  }, []);

  useEffect(() => {
    if (isPreviewMode && isEditingPrompt) {
      handlePromptEditEnd();
    }
  }, [isPreviewMode, isEditingPrompt, handlePromptEditEnd]);

  const handleDelete = useCallback(async () => {
    onNodeDelete(data.node);
  }, [data.node, onNodeDelete]);

  const handleTemperatureChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number.parseFloat(e.target.value);
      if (!Number.isNaN(value) && value >= 0 && value <= 1) {
        setTemperature(value);
      }
    },
    []
  );

  return (
    <div style={{ position: 'relative' }}>
      <BotBuilderNode
        title='LLM Prompt'
        onNodeDelete={handleDelete}
        deleteButtonRef={deleteButtonRef}
        isLocked={data.isLocked}
      >
        <div className='flex flex-col gap-3'>
          <div className='flex flex-col gap-1'>
            <Label className='text-xs font-medium text-gray-600 mb-1'>
              Prompt
            </Label>
            {!isPreviewMode && isEditingPrompt ? (
              <RichTextEditor
                textContent={prompt}
                onChange={handlePromptChange}
                onPreview={handlePromptEditEnd}
                isEditable
                showToolbar
                placeholder='Enter your prompt here...'
                outputFormat='plaintext'
              />
            ) : (
              <CustomTooltip content='Edit prompt'>
                <button
                  type='button'
                  onClick={handlePromptEditStart}
                  className='cursor-pointer w-full border-none p-0 text-left bg-white text-sm rounded-lg'
                >
                  {!prompt || prompt.trim() === '' ? (
                    <div className='px-3 py-2 text-sm text-muted-foreground'>
                      Enter your prompt here...
                    </div>
                  ) : (
                    <RichTextEditor
                      textContent={prompt}
                      isEditable={false}
                      showToolbar={false}
                      className='preview-mode text-sm'
                      outputFormat='plaintext'
                    />
                  )}
                </button>
              </CustomTooltip>
            )}
          </div>

          <div>
            <Label className='text-xs font-medium text-gray-600 mb-1'>
              AI model
            </Label>
            <CustomSelect
              placeholder='Select AI model...'
              value={aiModel}
              onValueChange={setAiModel}
              options={AI_MODEL_OPTIONS}
              className='bg-white'
              disabled={data.isLocked}
            />
          </div>

          <div>
            <Label className='text-xs font-medium text-gray-600 mb-1'>
              Temperature
            </Label>
            {supportsTemperature(aiModel) ? (
              <input
                type='number'
                min='0'
                max='1'
                step='0.05'
                className='w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                value={temperature}
                onChange={handleTemperatureChange}
                disabled={data.isLocked}
              />
            ) : (
              <p className='text-xs text-gray-500'>
                This model does not accept a temperature setting.
              </p>
            )}
          </div>

          <div>
            <Label className='text-xs font-medium text-gray-600 mb-1'>
              Output variable name
            </Label>
            <CustomInput
              placeholder='e.g. summary'
              value={outputVariableName}
              onChange={(e) => setOutputVariableName(e.target.value)}
              className='bg-white'
              disabled={data.isLocked}
            />
            <div className='text-xs text-gray-500 mt-1'>
              The LLM response will be saved to this variable
            </div>
          </div>

          <div>
            <Label className='text-xs font-medium text-gray-600 mb-1'>
              Output type
            </Label>
            <CustomSelect
              value={outputValueType}
              onValueChange={(value) =>
                setOutputValueType(value as SlotValueType)
              }
              options={[
                {
                  value: SlotValueType.FREE_TEXT,
                  label: SlotValueTypeLabels[SlotValueType.FREE_TEXT]
                },
                {
                  value: SlotValueType.BOOLEAN,
                  label: SlotValueTypeLabels[SlotValueType.BOOLEAN]
                }
              ]}
              className='bg-white'
              disabled={data.isLocked}
            />
            {outputValueType === SlotValueType.BOOLEAN && (
              <div className='text-xs text-gray-500 mt-1'>
                The model will return true or false. Write a yes/no question;
                don&apos;t ask for text formatting.
              </div>
            )}
          </div>

          <div>
            <Label className='text-xs font-medium text-gray-600 mb-1'>
              Error message (optional)
            </Label>
            <CustomInput
              placeholder='Custom error message...'
              value={errorMessage}
              onChange={(e) => setErrorMessage(e.target.value)}
              className='bg-white'
              disabled={data.isLocked}
            />
          </div>
        </div>
      </BotBuilderNode>
      <Handle
        type='target'
        position={Position.Top}
        style={{ top: -8 }}
        isConnectable={isConnectable}
        id='target-handle'
      />
      <Handle
        type='source'
        position={Position.Bottom}
        style={{ bottom: -8 }}
        isConnectable={isConnectable}
        id='source-handle'
      />
    </div>
  );
}

export default LlmNode;
